<template>
  <mce-editor
    type="freestyle-content"
    :tinymceScriptSrc="'https://assets-cdn-public.ahaslides.com/js/tinymce/5.10.9/tinymce.min.js'"
    :class="classComponent"
    :id="blockId"
    :ref="blockId"
    :init="initOptions"
    :value="formatTextForAnimation(dataValue)"
    :disabled="disabledEditor"
    :style="blockStyle"
    @onClick="onClick"
    @input="onInput"
    @onBlur="onBlur"
  />
</template>

<script>
import { get } from 'lodash'
import mceEditor from '@tinymce/tinymce-vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import debounce from 'debounce'
import * as Sentry from '@sentry/vue'
import eventCategories from '@/constant/eventCategories'
import configs from '@/config'
import utils from '@/util'
import EventBus from '@/eventBus'
import ClickableMixin from '@/mixins/text-editor/ClickableMixin'
import { deleteFakeDom } from '@/utils/dom.utils'
import PresentationUtils from '@/utils/presentation'

export default {
  components: {
    mceEditor,
  },
  mixins: [ClickableMixin],
  props: {
    value: {
      type: String,
      default: null,
    },
    slideId: {
      type: Number,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    blockId: {
      type: String,
      default: null,
    },
    blockStyle: {
      type: Object,
      default: null,
    },
    animation: {
      type: String,
      default: '',
    },
    isInfinity: {
      type: Boolean,
      default: false,
    },
    duration: {
      type: Number,
      default: 0,
    },
  },
  data: () => {
    return {
      disabledEditor: false,
      mceEditorKey: configs.mceEditorKey,
      isInputOnFocus: false,
      mceEditorInstance: null,
      lastCaret: null,
    }
  },

  computed: {
    ...mapGetters(['isPresenting', 'isBackstage', 'isTextColourLight']),
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      sharePresentation: (state) => state.sharePresentation,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      currentUser: (state) => state.currentUser,
      isShowLinkEditorModal: (state) => state.isShowLinkEditorModalV3,
    }),
    elementRef() {
      if (!this.blockId) return null
      if (!this.$refs[this.blockId]) return null
      return get(this, `$refs['${this.blockId}'].$el`, null)
    },
    animateClass() {
      if (!this.animation) return null
      return `animate__animated animate__${this.animation} ${this.isInfinity ? 'animate__infinite' : ''} ${this.duration ? `animate__delay-${this.duration}s` : ''} `
    },
    cacheKey() {
      return `${this.slideId}:${this.blockId}`
    },
    classComponent() {
      return {
        'element-editor': true,
        'rich-text-editor': true,
        'is-focused': this.isInputOnFocus,
        'mce-edit-focus': this.isInputOnFocus,
        'is-disabled': this.disabled,
        'light-text-colour': this.isTextColourLight,
        'is-sharing': this.sharePresentation,
        clickable: this.clickable,
      }
    },
    isPresentingOrBackstageOrShare() {
      return this.sharePresentation || this.isPresenting || this.isBackstage
    },
    isDisableEditor() {
      return this.isDisableEditPresentation || this.isPresentingOrBackstageOrShare
    },
  },

  watch: {
    isShowLinkEditorModal(val) {
      if (!val) {
        deleteFakeDom()
        this.onSaveEditHyperLink()
      }
    },
    isPresenting: function watchIsPresenting() {
      deleteFakeDom()
      if (!this.isPresenting) {
        this._setInputBlur()
      }
    },
    animateClass: function watchAnimateClass() {
      const editor = this.getValidatedInput()
      // get children of editor
      const { children } = editor
      if (children && children.length === 1) {
        const child = children[0]
        // check children is div
        if (child && child.tagName === 'DIV') {
          child.className = this.animateClass
        }
      }
    },
    disabled: {
      handler(value, oldValue) {
        if (value !== oldValue) {
          this.disabledEditor = value
        }
      },
      immediate: false,
    },
  },

  created() {
    const self = this
    // add wrapper in inner hmtl
    this.initOptions = {
      relative_urls: false,
      remove_script_host: false,
      menubar: false,
      inline: true,
      readonly: true,
      fixed_toolbar_container: '.freestyle-editor-toolbar',
      plugins: [
        'advlist autolink lists  charmap',
        'paste wordcount textpattern link_v3',
        // 'tiny_mce_wiris', // -> Math plugin, will be developed in Sprint 49
      ],
      // external_plugins: { // -> Math plugin, will be developed in Sprint 49
      //   tiny_mce_wiris: 'https://d25510ev64x81w.cloudfront.net/js/mathtype-plugin/plugin.min.js',
      // },
      // To enable mathtype, add tiny_mce_wiris_formulaEditor to the toolbar

      toolbar: `
         fontselect | fontsizeselect | bold italic underline strikethrough |
        forecolor backcolor |
        alignleft aligncenter alignright alignjustify |
        bullist numlist link_v3 charmap
      `,
      fontsize_formats: '12=16px 13=20px 14=24px 15=28px 16=32px 18=36px 20=40px 22=44px 24=48px 28=52px 32=56px 40=64px 50=70px 60=90px 70=110px 80=125px 90=140px',
      font_formats: 'Plus Jakarta Sans=Plus Jakarta Sans; Nunito=Nunito; Roboto=Roboto; Roboto Slab=Roboto Slab; Open Sans=Open Sans; Oswald=Oswald; Montserrat=Montserrat; Source Code Pro=Source Code Pro; Merriweather=Merriweather; EB Garamond=EB Garamond; Cairo=Cairo; Kanit=Kanit',
      default_link_target: '_blank',
      paste_data_images: false,
      paste_as_text: false,
      remove_linebreaks: false,
      textpattern_patterns: [
        { start: '#', format: 'h1' },
        { start: '##', format: 'h2' },
        { start: '###', format: 'h3' },
        { start: '####', format: 'h4' },
        { start: '#####', format: 'h5' },
        { start: '######', format: 'h6' },
        // The following text patterns require the `lists` plugin
        { start: '1. ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'decimal' } },
        { start: '1) ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'decimal' } },
        { start: 'a. ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'lower-alpha' } },
        { start: 'a) ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'lower-alpha' } },
        { start: 'A. ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'upper-alpha' } },
        { start: 'A) ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'upper-alpha' } },
        { start: 'i. ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'lower-roman' } },
        { start: 'i) ', cmd: 'InsertOrderedList', value: { 'list-style-type': 'lower-roman' } },
        { start: '* ', cmd: 'InsertUnorderedList' },
        { start: '- ', cmd: 'InsertUnorderedList' },
        { start: '**', end: '**', format: 'bold' },
        { start: '(c)', replacement: '©' },
      ],

      setup(editor) {
        self.mceEditorInstance = editor

        // window.tinymce.PluginManager.add('link_v2', (editor) => {
        //   editor.ui.registry.addButton('link_v2', {
        //     shortcut: 'Meta+K',
        //     tooltip: 'Insert / edit link',
        //     icon: 'link',

        //     onAction() {
        //       self.$store.state.isShowLinkEditorModal = true
        //       self.updateFakeSelection()
        //     },
        //   })
        // })

        // Add new link_v3 plugin with improved selection handling
        window.tinymce.PluginManager.add('link_v3', (editor) => {
          editor.ui.registry.addButton('link_v3', {
            shortcut: 'Meta+K',
            tooltip: 'Insert / edit link',
            icon: 'link',

            onAction() {
              // Only proceed if button is not disabled
              if (!self.hasSelectionMixedContent()) {
                self.$store.state.isShowLinkEditorModalV3 = true
                self.updateFakeSelectionV3()
              }
            },

            onSetup(api) {
              try {
                // Function to update button state based on selection
                const updateButtonState = () => {
                  const hasMixedContent = self.hasSelectionMixedContent()
                  const isInLink = self.isCursorInLink()

                  // Disable button if mixed content
                  api.setDisabled(hasMixedContent)

                  // Highlight button if cursor is in a link (and not disabled)
                  if (isInLink && !hasMixedContent) {
                    self.highlightLinkButton(true)
                  } else {
                    self.highlightLinkButton(false)
                  }
                }

                // Update button state when selection changes
                editor.on('SelectionChange', updateButtonState)
                editor.on('NodeChange', updateButtonState)

                // Initial state update
                updateButtonState()

                // Cleanup when button is destroyed
                return () => {
                  editor.off('SelectionChange', updateButtonState)
                  editor.off('NodeChange', updateButtonState)
                }
              } catch (e) {
                console.error(e)
              }
            },
          })
        })

        editor.on('ExecCommand', (e) => {
          self.handleTrackEditing(e.command, e.value)
          const cmd = e.command
          // Workaround to edit font size for list marker equals to list content
          if (cmd === 'FontSize') {
            const fontSize = e.value
            const fontSizeNumber = e.value.replace(/em|px|rem|pt/g, '')
            const node = e.target.selection.getNode()
            const nodeParent = node.parentNode
            if (node.nodeName === 'SPAN' && nodeParent.nodeName === 'LI') {
              editor.dom.setStyle(nodeParent, 'font-size', fontSize)
            } else if (node.nodeName === 'UL' || node.nodeName === 'OL') {
              const li = editor.dom.select('li', node)
              editor.dom.setStyle(li, 'font-size', fontSize)
              editor.dom.setStyle(node, 'padding-left', `${fontSizeNumber * 2 < 60 ? 60 : fontSizeNumber * 2}px`)
            }
          }
          if (cmd === 'mceFocus') {
            // clear the open state from last focus
            const dialogs = document.getElementsByClassName('tox-dialog-wrap')
            if (dialogs && dialogs[0]) {
              dialogs[0].remove()
            }
          }
        })

        editor.on('init', () => {
          self.$emit('ready', self.slideId)
          self.anchorClickableLink(self.value)
          if (self.isDisableEditor) {
            self.disabledEditor = true
          }
        })
        editor.on('paste', (e) => {
          const paste = (e.clipboardData || window.clipboardData).getData('text')
          // Prevent paste images from other source
          // Only allow pasting text
          if (!paste) {
            e.preventDefault()
          }
        })

        // AHA-19070 Prevent unexpected errors because we don't listen the 'focus' event
        // We only listen the 'click' event to control the state of the RichTextEditor
        editor.off('focus')
      },

    }
  },

  mounted() {
    EventBus.$on(`focus-editor-${this.blockId}`, () => {
      EventBus.$emit('show-freestyle-editor-toolbar', true)
      this.onFocus()
      this.mceEditorInstance.selection.select(this.mceEditorInstance.getBody(), true)
    })

    this.registerClickHyperLinkEvent()
    deleteFakeDom()
  },

  beforeDestroy() {
    if (this.sharePresentation || !this.presentation.id) return
    this.onBlur()
  },

  destroyed() {
    EventBus.$off(`focus-editor-${this.blockId}`)
    deleteFakeDom()
    this.unregisterClickHyperLinkEvent()
  },

  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction', 'blockHistoryAction', 'unblockHistoryAction', 'setClickedUrlAction',
      'patchPresentationAction']),
    onSaveEditHyperLink() {
      const activeSlideBlocks = get(this, 'slideActive.canvasBlocks', {})
      const currentBlockId = get(this, 'blockId', '')
      if (activeSlideBlocks[currentBlockId]) {
        this.onUpdateBodyContent()
        this.clearBlockContentCache(this.cacheKey)
      }
    },
    registerClickHyperLinkEvent() {
      const contentArea = this.elementRef
      if (!contentArea) {
        return
      }
      contentArea.addEventListener('click', this.handleClickHyperLink)
    },
    unregisterClickHyperLinkEvent() {
      const contentArea = this.elementRef
      if (!contentArea) {
        return
      }
      contentArea.removeEventListener('click', this.handleClickHyperLink)
    },
    handleClickHyperLink(e) {
      const el = get(e, 'target')
      const hasClass = el.classList.contains('text-hyper-link')
      const { href } = el
      if (href && hasClass && this.isPresenting) {
        this.setClickedUrlAction(href)
        e.preventDefault()
        e.stopImmediatePropagation()
      }
    },
    formatTextForAnimation(text) {
      if (!this.animateClass) {
        return text
      }
      if (text && text.includes('<div class="animate__animated')) {
        return text
      }
      return `<div class="${this.animateClass} ">${text}</div>`
    },
    updateFakeSelection() {
      deleteFakeDom()
      const selection = window.getSelection()
      if (selection.rangeCount > 0) {
        this.createFakeDom()
        // Update the fake selection position and size
      }
    },

    updateFakeSelectionV3() {
      deleteFakeDom()
      const selection = window.getSelection()
      if (selection.rangeCount > 0) {
        this.createFakeDomV3()
      }
    },

    onChangeEditorValue: debounce(function debounceChangeEditorValue() {
      this.onUpdateBodyContent()
      this.clearBlockContentCache(this.cacheKey)
    }, 500, { leading: true }),

    onInputEditor: debounce(function debounceInput() {
      const editor = this.getValidatedInput()
      if (!editor ||
          editor.innerHTML === this.value) return
      this.cacheBlockContent(this.cacheKey, editor.innerHTML)
    }, 300),

    cacheBlockContent(cacheKey, blockContent) {
      if (!this.$store.state.blockContentCache) {
        this.$store.state.blockContentCache = {}
      }
      this.$store.state.blockContentCache[cacheKey] = this.formatTextForAnimation(blockContent)
    },
    clearBlockContentCache(cacheKey) {
      if (this.$store.state.blockContentCache && this.$store.state.blockContentCache[cacheKey]) {
        delete this.$store.state.blockContentCache[cacheKey]
      }
    },

    getValidatedInput() {
      if (!this.mceEditorInstance) return null
      return this.mceEditorInstance.contentAreaContainer
    },

    async onUpdateBodyContent() {
      try {
        const editor = this.getValidatedInput()
        if (!editor ||
            editor.innerHTML === this.value) return
        this.setSyncLoadingAction(1)
        this.updateValueBlockEditor(editor.innerHTML)
        PresentationUtils.handleSuggestPresentationTitle(editor.innerText)
        this.setSyncLoadingAction(2)
        this._handleTrackGAAfterUpdate()
      } catch (error) {
        Sentry.captureException(error)
        this.setSyncLoadingAction(3)
      }
    },

    updateValueBlockEditor(value) {
      this.$emit('rich-text-editor-update', this.formatTextForAnimation(value))
    },

    onBlur() {
      this.unblockHistoryAction()
      this.isInputOnFocus = false
      this.lastCaret = null
      window.getSelection().empty()
      this.onChangeEditorValue()
      setTimeout(() => {
        this.$emit('onBlur')
        EventBus.$emit('blur-editing-box')
      }, 100)
    },

    _setInputBlur() {
      setTimeout(() => {
        const editor = document.getElementById(this.blockId)
        if (editor) {
          editor.blur()
        }
      }, 0)
    },

    onClick(e) {
      // Only accept 'click' event trigger from MoveableBlockEditor to focus the editor
      if (!e.isTrusted) {
        this.onFocus()
        this.mceEditorInstance.focus()
      }
    },

    onInput() {
      this.onInputEditor()
    },

    onFocus() {
      this.blockHistoryAction()
      const editor = document.getElementById(this.blockId)
      if (!editor) return
      this.isInputOnFocus = true
      this.$emit('onFocusEditor')

      if (!this.value ||
          utils.extractContent(this.value, true).includes('Click to add') ||
          utils.extractContent(this.value, true).includes('Replace this with')
      ) {
        this.mceEditorInstance.selection.select(this.mceEditorInstance.getBody(), true)
      }

      // Nếu chưa focus và không phải đang bôi đen thì sẽ cho con trỏ về cuối.
      if (!this.mceEditorInstance.selection.getContent().length && !this.lastCaret) {
        this.mceEditorInstance.selection.select(this.mceEditorInstance.getBody(), true)
        this.mceEditorInstance.selection.collapse(false)
      }
      this.lastCaret = this.mceEditorInstance.selection.getSel().focusOffset
    },

    handleTrackEditing(command, value) {
      const trackableCommands = [
        'mceApplyTextColor',
        'FontSize',
        'FontName',
        'mceToggleFormat',
        'mceApplyTextcolor',
        'JustifyCenter',
        'JustifyFull',
        'JustifyLeft',
        'JustifyRight',
        'InsertOrderedList',
        'InsertUnorderedList',
        'mceInsertLink',
        'mceShowCharmap',
      ]
      if (!trackableCommands.includes(command)) return
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        `Edit slide body > ${command}`,
        value,
        this.presentation.id,
        {
          slideId: this.slideActive.id,
        },
      )
    },

    _handleTrackGAAfterUpdate() {
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Edit slide body > Edit text',
        '',
        this.presentation.id,
        {
          slideId: this.slideActive.id,
        },
      )
    },
    createFakeDom() {
      const selectObj = window.getSelection()
      const selectedText = selectObj.toString()
      const range = selectObj.getRangeAt(0)
      // create fakeDom
      const fakeDom = document.createElement('span')
      fakeDom.innerHTML = selectedText
      fakeDom.classList.add('fake-selection')
      const newRange = document.createRange()
      newRange.selectNode(range.startContainer)

      try {
        range.surroundContents(fakeDom)
      } catch (e) {
        Sentry.captureException(e)
        // error when range.surroundContents is not supported
        newRange.surroundContents(fakeDom)
      }
    },

    createFakeDomV3() {
      const selectObj = window.getSelection()
      const selectedText = selectObj.toString()
      const range = selectObj.getRangeAt(0)

      // Check if the selection is within an existing link
      let linkElement = null
      let currentNode = range.commonAncestorContainer

      // Traverse up the DOM tree to find if we're inside a link
      while (currentNode && currentNode.nodeType !== Node.DOCUMENT_NODE) {
        if (currentNode.nodeType === Node.ELEMENT_NODE && currentNode.tagName === 'A') {
          linkElement = currentNode
          break
        }
        currentNode = currentNode.parentNode
      }

      // If we're editing an existing link, preserve the link structure
      if (linkElement) {
        // Create fake selection that encompasses the entire link
        const fakeDom = document.createElement('span')
        fakeDom.innerHTML = linkElement.innerHTML
        fakeDom.classList.add('fake-selection')

        // Store original link properties as data attributes for easy retrieval
        fakeDom.setAttribute('data-original-href', linkElement.getAttribute('href') || '')
        fakeDom.setAttribute('data-original-target', linkElement.getAttribute('target') || '_self')
        fakeDom.setAttribute('data-original-text', linkElement.textContent || '')
        // Store the complete original link HTML for restoration on cancel
        fakeDom.setAttribute('data-original-link-html', linkElement.outerHTML)

        // Replace the link with our fake selection
        linkElement.replaceWith(fakeDom)
      } else {
        // No existing link - use improved fallback for new links
        const fakeDom = document.createElement('span')

        if (selectedText) {
          fakeDom.innerHTML = selectedText
        } else {
          // If no text is selected, create a placeholder that can be found and replaced
          fakeDom.innerHTML = '&nbsp;' // Use non-breaking space to ensure visibility
        }

        fakeDom.classList.add('fake-selection')
        fakeDom.setAttribute('data-original-text', selectedText || '')

        // Check if the range is collapsed (no text selected)
        if (range.collapsed) {
          // For collapsed ranges (cursor position), insert the fake DOM directly
          range.insertNode(fakeDom)
          // Move cursor after the fake DOM to prevent issues
          range.setStartAfter(fakeDom)
          range.setEndAfter(fakeDom)
          const selection = window.getSelection()
          selection.removeAllRanges()
          selection.addRange(range)
        } else {
          // For actual text selections, try to surround the content
          try {
            range.surroundContents(fakeDom)
          } catch (e) {
            // Fallback for complex selections
            const contents = range.extractContents()
            fakeDom.appendChild(contents)
            range.insertNode(fakeDom)
          }
        }
      }
    },
    hasSelectionMixedContent() {
      try {
        const selection = window.getSelection()
        if (selection.rangeCount === 0) return false

        const range = selection.getRangeAt(0)

        // If nothing is selected, allow link creation
        if (range.collapsed) return false

        // Get the selected content as a document fragment
        const fragment = range.cloneContents()

        // Check what types of content are in the selection
        let hasLinks = false
        let hasPlainText = false

        // Function to recursively check nodes
        const checkNode = (node) => {
          if (node.nodeType === Node.TEXT_NODE) {
          // If it's a text node with actual content, mark as plain text
            if (node.textContent.trim()) {
              hasPlainText = true
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.tagName === 'A') {
              hasLinks = true
            }
            // Recursively check child nodes
            for (let i = 0; i < node.childNodes.length; i++) {
              checkNode(node.childNodes[i])
            }
          }
        }

        // Check all nodes in the fragment
        for (let i = 0; i < fragment.childNodes.length; i++) {
          checkNode(fragment.childNodes[i])
        }

        // If we have both links and plain text, it's mixed content
        return hasLinks && hasPlainText
      } catch (e) {
        return false
      }
    },
    isCursorInLink() {
      const selection = window.getSelection()
      if (selection.rangeCount === 0) return false

      const range = selection.getRangeAt(0)

      // Check if the current node or any parent is a link
      let currentNode = range.startContainer

      // If it's a text node, start from its parent
      if (currentNode.nodeType === Node.TEXT_NODE) {
        currentNode = currentNode.parentNode
      }

      // Traverse up the DOM tree to find if we're inside a link
      while (currentNode && currentNode.nodeType !== Node.DOCUMENT_NODE) {
        if (currentNode.nodeType === Node.ELEMENT_NODE && currentNode.tagName === 'A') {
          return true
        }
        currentNode = currentNode.parentNode
      }

      return false
    },
    highlightLinkButton(highlight) {
      try {
        // Find the link button in the toolbar using multiple possible selectors
        const selectors = [
          '[aria-label="Insert / edit link"]',
          '[title="Insert / edit link"]',
          'button[title*="link"]',
          '.tox-tbtn[aria-label*="link"]',
          'button[data-mce-name="link_v3"]',
        ]

        let linkButton = null
        for (let i = 0; i < selectors.length; i++) {
          linkButton = document.querySelector(selectors[i])
          if (linkButton) break
        }

        if (linkButton) {
          if (highlight) {
            linkButton.classList.add('tox-tbtn--enabled')
          } else {
            linkButton.classList.remove('tox-tbtn--enabled')
          }
        }
      } catch (e) {
        console.warn('Could not highlight link button:', e)
      }
    },
  },

}

</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";

.rich-text-editor {
  overflow: hidden;
  width: 100%;
  font-size: 2rem;
  padding: 10px;
  min-height: 42px;
  min-width: 42px;
  -webkit-user-select: text; // Fix bug: editor doesn't work on Safari
  cursor: text;
  line-height: 1.2;
  &.is-disabled {
    border: none;
    cursor: default;
  }
  &:hover {
    overflow-y: auto;
  }

  &::v-deep .mce-clonedresizable {
    position: absolute !important;
  }
  &::v-deep .mce-resize-helper {
    display: none !important;
  }
  &::v-deep img {
    outline: none !important;
  }

}
.slide-container {

  .freestyle-canvas-content {

    .is-sharing.mce-content-body {
      ::v-deep [data-mce-selected="inline-boundary"]{
        background-color: inherit;
      }
    }
  }
}

</style>
